import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldAlert } from 'lucide-react';
import {
  REMEDIATION_LIMIT_KEYS, REMEDIATION_LIMIT_LABELS, REMEDIATION_PAUSE_CHOICES, REMEDIATION_PAUSE_FOREVER, REMEDIATION_PAUSE_LABELS, REMEDIATION_STEP_LABELS,
  REMEDIATION_WAITING_LABELS, type RemediationLimits, type RemediationPauseChoice, type RemediationStep,
} from '@evi/shared';
import { getDisplayErrorMessage } from '@/lib/api';
import { actionLine, fetchChargerLadder, saveChargerLadder } from '@/lib/remediation';
import styles from './ChargerDetailPage.module.css';

/**
 * One charger's automatic fixes (Warren, 28 Oct 2026, evening addendum item 1: "limits per charger"). Super Admin only
 * (the Api answers 403 to anyone else, and the card then shows nothing): the problem the ladder is on, who it is
 * waiting on, the last actions and their results, a pause (1 hour, 24 hours, 7 days or until resumed), and this
 * charger's own limits, which can only be lower than the network's. Every change is audited on the charger.
 */
const PHASE_LABEL: Record<string, string> = {
  confirm: 'Reading the status again', act: 'Choosing the next fix', verify: 'Checking whether the last fix worked', asked: 'At the breaker step',
  escalated: 'Service call open', offline: 'Offline after a fix', stalled: 'Nothing more to try automatically',
};

export function ChargerAutoFixCard({ chargerId }: { chargerId: string }) {
  const client = useQueryClient();
  const key = ['charger-remediation', chargerId];
  const q = useQuery({ queryKey: key, queryFn: () => fetchChargerLadder(chargerId), retry: false, staleTime: 30_000 });
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [limits, setLimits] = useState<RemediationLimits | null>(null);
  if (q.isError) return null;
  async function apply(body: Parameters<typeof saveChargerLadder>[1]) {
    setBusy(true); setError('');
    try { await saveChargerLadder(chargerId, body); await client.invalidateQueries({ queryKey: key }); setLimits(null); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'Not saved.')); } finally { setBusy(false); }
  }
  const v = q.data;
  const paused = v?.paused_until && new Date(v.paused_until).getTime() > Date.now();
  const forever = paused && new Date(v!.paused_until!).getTime() >= REMEDIATION_PAUSE_FOREVER.getTime();
  const shown = limits ?? v?.limits ?? null;
  return (
    <div className={styles.dlmCap}>
      <div className={styles.dlmCapHead}>
        <ShieldAlert size={16} />
        <span className={styles.dlmCapTitle}>Automatic fixes</span>
        {v?.waiting_on && <span className={styles.dlmCapValue}>{REMEDIATION_WAITING_LABELS[v.waiting_on]}</span>}
      </div>
      {q.isLoading || !v ? <p className={styles.dlmCapLine}><Loader2 size={13} /> Loading.</p> : <>
        {v.episode?.is_open
          ? <p className={styles.dlmCapLine}>Working on: <strong>{v.episode.trigger.kind.replace(/_/g, ' ')}</strong>
            {v.episode.trigger.connector_id != null ? <> on connector {v.episode.trigger.connector_id}</> : null} since {new Date(v.episode.opened_at).toLocaleString()}.{' '}
            {PHASE_LABEL[v.episode.phase] ?? v.episode.phase}{v.episode.step ? <> (last step: {REMEDIATION_STEP_LABELS[v.episode.step]}{v.episode.mode === 'shadow' ? ', shadow' : ''})</> : null}.</p>
          : <p className={styles.dlmCapLine}>Nothing to fix right now.{v.episode?.closed_at ? <> The last problem closed {new Date(v.episode.closed_at).toLocaleString()} ({(v.episode.close_reason ?? '').replace(/_/g, ' ')}).</> : null}</p>}
        <p className={styles.dlmCapLine}>
          {paused ? <>Paused {forever ? 'until resumed' : <>until {new Date(v.paused_until!).toLocaleString()}</>}. </> : null}
          {REMEDIATION_PAUSE_CHOICES.map((c: RemediationPauseChoice) => <button key={c} type="button" disabled={busy} onClick={() => void apply({ pause: c })} style={{ marginRight: 6 }}>
            {REMEDIATION_PAUSE_LABELS[c]}</button>)}
          {paused && <button type="button" disabled={busy} onClick={() => void apply({ pause: 'resume' })}>Resume</button>}
        </p>
        {shown && <div className={styles.dlmCapLine} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          {REMEDIATION_LIMIT_KEYS.map((k) => <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {REMEDIATION_LIMIT_LABELS[k]}
            <input type="number" min={0} max={v.network_limits[k]} step={1} value={shown[k]} style={{ width: 56 }} disabled={busy}
              onChange={(e) => setLimits({ ...shown, [k]: Math.max(0, Math.min(v.network_limits[k], Number.parseInt(e.target.value || '0', 10) || 0)) })} />
          </label>)}
          <button type="button" disabled={busy || !limits} onClick={() => limits && void apply({ limits })}>Save limits</button>
        </div>}
        {error && <p className={styles.dlmCapLine} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
        {v.actions.length > 0 && <ul className={styles.dlmCapLine} style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          {v.actions.slice(0, 10).map((a) => <li key={a.id}>
            {new Date(a.at).toLocaleString()}: {actionLine(a, REMEDIATION_STEP_LABELS as Record<RemediationStep, string>)}
            {a.cleared === null ? '' : a.cleared ? (a.mode === 'shadow' ? ', cleared on its own' : ', cleared') : ', did not clear'}
          </li>)}
        </ul>}
      </>}
    </div>
  );
}
